import { useEffect, useState, useCallback } from "react";
import api, { API_BASE } from "../../lib/api";
import { CaretLeft, CaretRight, PlayCircle, X } from "@phosphor-icons/react";

const imgUrl = (rel) => {
  if (!rel) return "";
  try { return new URL(rel, API_BASE).toString(); } catch { return rel; }
};

/**
 * Homepage "See it in action" — restored to step-by-step walkthroughs only.
 * The YouTube video block was removed per user request; admins can still
 * manage videos via Admin → Landing videos but they no longer appear here.
 */
export default function SeeItInAction() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // walkthrough object opened in modal

  useEffect(() => {
    let alive = true;
    api.get("/public/walkthroughs/featured")
      .then(({ data }) => { if (alive) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { /* feature optional — landing should still render */ });
    return () => { alive = false; };
  }, []);

  if (!items.length) return null;

  return (
    <section id="see-it" className="border-b hairline" data-testid="see-it-in-action-section">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow mb-2">See it in action</div>
            <h2 className="font-serif text-3xl sm:text-4xl">Watch Estima do the math, not the marketing.</h2>
            <p className="text-muted-foreground mt-2 max-w-xl text-sm">
              {items.length} guided demos using real ₹-values from a sample portfolio. No sign-up required.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((w) => (
            <button
              key={w.slug}
              onClick={() => setActive(w)}
              className="card-flat p-0 overflow-hidden text-left hover:border-[hsl(var(--secondary))] transition group"
              data-testid={`see-it-card-${w.slug}`}
            >
              <div className="aspect-video bg-muted relative overflow-hidden">
                {w.steps?.[0]?.image_url ? (
                  <img
                    src={imgUrl(w.steps[0].image_url)}
                    alt={w.title}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition duration-500"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90" />
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))] bg-black/60 px-2 py-1">
                    {w.category}
                  </span>
                  <span className="text-[10px] text-white/80 bg-black/60 px-2 py-1 inline-flex items-center gap-1">
                    <PlayCircle size={12} weight="fill" /> {w.step_count} step{w.step_count === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="font-serif text-lg mb-1 group-hover:text-[hsl(var(--secondary))] transition">{w.title}</div>
                <p className="text-xs text-muted-foreground line-clamp-2">{w.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && <SeeItModal walkthrough={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function SeeItModal({ walkthrough, onClose }) {
  const steps = walkthrough.steps || [];
  const [idx, setIdx] = useState(0);
  const total = steps.length;
  const step = steps[idx];

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [next, prev, onClose]);

  if (!step) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="see-it-modal"
    >
      <div
        className="bg-card border hairline w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 sm:px-6 py-3 border-b hairline flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-[hsl(var(--secondary))] text-[10px]">{walkthrough.category}</div>
            <div className="font-serif text-base sm:text-lg truncate">{walkthrough.title}</div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
            data-testid="see-it-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto bg-muted">
          {step.image_url ? (
            <img
              src={imgUrl(step.image_url)}
              alt={step.title}
              className="w-full h-auto"
            />
          ) : (
            <div className="aspect-video flex items-center justify-center text-muted-foreground text-sm">
              No screenshot for this step yet.
            </div>
          )}
        </div>

        <div className="px-5 sm:px-7 py-4 border-t hairline flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Step {idx + 1} of {total}
            </div>
            <div className="font-serif text-base sm:text-lg truncate">{step.title}</div>
            {step.caption && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 sm:line-clamp-none">
                {step.caption}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={prev}
              disabled={idx === 0}
              className="btn-ghost p-2 disabled:opacity-30"
              aria-label="Previous"
              data-testid="see-it-prev"
            >
              <CaretLeft size={16} />
            </button>
            <button
              onClick={next}
              disabled={idx === total - 1}
              className="btn-primary px-4 py-2 text-xs disabled:opacity-50"
              data-testid="see-it-next"
            >
              {idx === total - 1 ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
